/**
 * Local TTS Service
 *
 * Provides text-to-speech via a local server (e.g., Kokoro on Jetson Thor).
 * Supports the OpenAI-compatible REST API: POST /v1/audio/speech
 *
 * Unlike ElevenLabs which uses a persistent multi-context WebSocket,
 * this service makes one HTTP request per sentence and reads each
 * sentence's PCM audio whole (the server sends it with a Content-Length).
 *
 * The local TTS server is accessed through nginx at /local-tts/.
 */

export interface LocalTTSConfig {
  /** Base path for local TTS API (default: '/local-tts') */
  basePath?: string
  /** Voice to use (default: 'af_heart') */
  voice?: string
  /** Sample rate of the output audio (default: 24000) */
  sampleRate?: number
}

export class LocalTTSService {
  private basePath: string
  private voice: string
  private sampleRate: number
  /** One controller per request in flight, so cancel() reaches all of them. */
  private controllers = new Set<AbortController>()

  constructor(config: LocalTTSConfig = {}) {
    this.basePath = config.basePath || '/local-tts'
    this.voice = config.voice || 'af_heart'
    this.sampleRate = config.sampleRate || 24000
  }

  get outputSampleRate(): number {
    return this.sampleRate
  }

  /**
   * Synthesize text and return the complete PCM audio as an ArrayBuffer.
   * Whole-sentence reads avoid byte-alignment issues with streaming chunks
   * (Int16 PCM requires 2-byte alignment).
   */
  async synthesizeToBuffer(
    text: string,
    voice?: string
  ): Promise<ArrayBuffer> {
    const controller = new AbortController()
    this.controllers.add(controller)
    try {
      const response = await fetch(`${this.basePath}/v1/audio/speech`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          input: text,
          voice: voice || this.voice,
          response_format: 'pcm',
        }),
        signal: controller.signal,
      })

      if (!response.ok) {
        throw new Error(`Local TTS synthesis failed: ${response.status}`)
      }

      return await response.arrayBuffer()
    } finally {
      this.controllers.delete(controller)
    }
  }

  /**
   * Cancel every synthesis in progress.
   */
  cancel(): void {
    for (const controller of this.controllers) {
      controller.abort()
    }
    this.controllers.clear()
  }

  /**
   * Fetch available voices from the local TTS server.
   */
  async getVoices(): Promise<Array<{ id: string; name: string }>> {
    try {
      const response = await fetch(`${this.basePath}/v1/voices`)
      if (!response.ok) return []
      const data = await response.json()
      return data.voices || []
    } catch {
      return []
    }
  }

  /**
   * Check if the local TTS server is healthy.
   */
  async isHealthy(): Promise<boolean> {
    try {
      const response = await fetch(`${this.basePath}/health`, {
        signal: AbortSignal.timeout(5000),
      })
      return response.ok
    } catch {
      return false
    }
  }
}
